import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  CalendarIcon, 
  FileSpreadsheet, 
  Upload, 
  Edit, 
  Download, 
  Trash2,
  Eye
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MemberList from "@/components/MemberList";
import NavHeader from "@/components/NavHeader";
import AnimatedPage from "@/components/AnimatedPage";
import PaymentTracker from "@/components/PaymentTracker";
import { getChitPoolById, createMember, updateMember, deleteMember, toggleMemberLift, deleteChitPool } from "@/lib/api";
import { ChitPool, Member, NewMember } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInMonths, addMonths } from "date-fns";
import { downloadMemberTemplate } from "@/lib/excelUtils";
import { useSubscription } from "@/lib/SubscriptionContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const ChitPoolDescription = ({ description }: { description?: string }) => {
  if (!description) return null;
  
  return (
    <Card className="mb-6 bg-card/60 backdrop-blur-sm border border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium text-left">Description</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground text-left">{description}</p>
      </CardContent>
    </Card>
  );
};

export const calculateMonthsCompleted = (firstDueDate: string) => {
  const startDate = new Date(firstDueDate);
  const currentDate = new Date();
  
  if (startDate > currentDate) return 0;
  
  return differenceInMonths(currentDate, startDate);
};

const ChitPoolDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { planLimits, userPlan } = useSubscription();
  const [chitPool, setChitPool] = useState<ChitPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMonthlyAmountsDialog, setShowMonthlyAmountsDialog] = useState(false);

  const fetchData = async () => {
    try {
      if (!id) return;
      setLoading(true);
      const data = await getChitPoolById(id);
      setChitPool(data);
    } catch (error) {
      console.error("Error fetching chit pool details:", error);
      toast({
        title: "Error",
        description: "Failed to load chit pool details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleAddMember = async (memberData: NewMember) => {
    try {
      if (!chitPool) return;
      
      if (chitPool.members.length >= planLimits.maxMembers) {
        toast({
          title: "Subscription Limit Reached",
          description: `Your ${userPlan?.name || "Free"} plan allows up to ${planLimits.maxMembers} members per chit fund. Please upgrade to add more members.`,
          variant: "destructive",
        });
        return;
      }
      
      await createMember(memberData);
      toast({
        title: "Success",
        description: "Member added successfully",
      });
      fetchData();
    } catch (error) {
      console.error("Error adding member:", error);
      if (error instanceof Error && error.message.includes("plan limit")) {
        toast({
          title: "Subscription Limit Reached",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add member",
          variant: "destructive",
        });
      }
    }
  };

  const handleUpdateMember = async (
    id: string, 
    data: Partial<{
      name: string;
      whatsappNumber: string;
      chitPoolId: string;
      disbursedAmount: number | null;
    }>
  ) => {
    try {
      if (data.disbursedAmount !== undefined) {
        await updateMember(id, {
          ...data,
          disbursedAmount: data.disbursedAmount
        });
      } else {
        await updateMember(id, data);
      }
      
      fetchData();
      toast({
        title: "Success",
        description: "Member updated successfully"
      });
    } catch (error) {
      console.error("Error updating member:", error);
      toast({
        title: "Error",
        description: "Failed to update member",
        variant: "destructive"
      });
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    try {
      await deleteMember(memberId);
      toast({
        title: "Success",
        description: "Member deleted successfully",
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting member:", error);
      toast({
        title: "Error",
        description: "Failed to delete member",
        variant: "destructive",
      });
    }
  };

  const handleToggleLift = async (memberId: string, hasLifted: boolean, disbursedAmount?: number | null) => {
    try {
      const updatedMember = await toggleMemberLift(memberId, hasLifted, disbursedAmount);
      toast({
        title: "Success",
        description: `Member ${hasLifted ? "marked as lifted" : "marked as not lifted"} successfully`,
      });
      fetchData();
    } catch (error) {
      console.error("Error toggling member lift status:", error);
      toast({
        title: "Error",
        description: "Failed to update member lift status",
        variant: "destructive",
      });

    }
  };

  const handleUpdateDisbursedAmount = async (memberId: string, amount: number) => {
    try {
      if (!chitPool) return;

      const memberData = chitPool.members.find((m) => m.id === memberId);
      if (!memberData) throw new Error("Member not found");

      await updateMember(memberId, { disbursedAmount: amount });
      toast({
        title: "Success",
        description: "Member's disbursed amount updated successfully",
      });
      fetchData();
    } catch (error) {
      console.error("Error updating member's disbursed amount:", error);
      toast({ title: "Error", description: "Failed to update disbursed amount", variant: "destructive" });
    }
  };

  const handleExcelUpload = () => {
    toast({
      title: "Info",
      description: "Excel upload functionality is not yet implemented",
    });
  };

  const handleExcelDownload = () => {
    if (!chitPool) return;
    downloadMemberTemplate(chitPool.name);
    toast({
      title: "Success",
      description: "Excel template downloaded successfully",
    });
  };

  const handleDeleteChitPool = async () => {
    if (!id) return;
    
    setIsDeleting(true);
    try {
      await deleteChitPool(id);
      toast({
        title: "Success",
        description: "Chit pool deleted successfully",
      });
      navigate("/chit-pools");
    } catch (error) {
      console.error("Error deleting chit pool:", error);
      toast({
        title: "Error",
        description: "Failed to delete chit pool",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!chitPool) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-xl text-muted-foreground">Chit pool not found</p>
        <Button variant="outline" onClick={() => navigate("/chit-pools")}>
          Back to Chit Pools
        </Button>
      </div>
    );
  }

  const monthsCompleted = calculateMonthsCompleted(chitPool.firstDueDate);
  const isAtMemberLimit = chitPool.members.length >= planLimits.maxMembers;

  return (
    <AnimatedPage>
      <div className="container max-w-5xl py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <NavHeader
            title={chitPool.name}
            subtitle={`₹${chitPool.amount.toLocaleString()} chit for ${chitPool.months} months`}
            backTo="/chit-pools"
          />
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(`/chit-pools/${id}/edit`)}
              className="gap-1 sm:gap-2"
            >
              <Edit className="h-4 w-4" />
              <span className="hidden sm:inline">Edit Details</span>
              <span className="sm:hidden">Edit</span>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="gap-1 sm:gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the chit pool
                    and all of its associated members.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteChitPool}
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? 
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                        Deleting...
                      </span> 
                      : 'Delete'
                    }
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {isAtMemberLimit && (
          <Alert className="bg-amber-50 border-amber-200 mb-4">
            <AlertTitle className="text-amber-800">Member Limit Reached</AlertTitle>
            <AlertDescription className="text-amber-700">
              Your {userPlan?.name || "Free"} plan allows up to {planLimits.maxMembers} members per chit fund. 
              <Button 
                variant="link" 
                className="text-primary p-0 h-auto ml-1"
                onClick={() => navigate("/#pricing")}
              >
                Upgrade your plan
              </Button> to add more members.
            </AlertDescription>
          </Alert>
        )}

        {chitPool.description && <ChitPoolDescription description={chitPool.description} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="col-span-1 lg:col-span-1">
            <CardHeader className="text-left">
              <CardTitle className="text-lg">Chit Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">Total Amount</span>
                <span className="font-medium">₹{chitPool.amount.toLocaleString()}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">Duration</span>
                <span className="font-medium">{chitPool.months} months</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">Months Completed</span>
                <span className="font-medium">{Math.min(monthsCompleted, chitPool.months)} / {chitPool.months}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">
                  {chitPool.sameAmountMonthly ? "Monthly Contribution" : `Month ${Math.min(monthsCompleted + 1, chitPool.months)} Contribution`}
                </span>
                <span className="font-medium">
                  ₹{chitPool.sameAmountMonthly 
                    ? chitPool.monthlyAmount.toLocaleString() 
                    : (chitPool.monthlyAmounts?.[Math.min(monthsCompleted, chitPool.months - 1)]?.amount || 0).toLocaleString()}
                </span>
              </div>
              {!chitPool.sameAmountMonthly && chitPool.monthlyAmounts && (
                <>
                  <Separator />
                  <div>
                    <span className="text-sm text-muted-foreground text-left block mb-2">All Monthly Amounts</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setShowMonthlyAmountsDialog(true)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View All Monthly Amounts
                    </Button>
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">Post-Lift Contribution</span>
                <span className="font-medium">₹{chitPool.postLiftAmount.toLocaleString()}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">First Due Date</span>
                <span className="font-medium flex items-center gap-1">
                  <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(chitPool.firstDueDate), "dd MMM yyyy")}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground text-left">Created On</span>
                <span className="font-medium flex items-center gap-1">
                  <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(chitPool.createdAt), "dd MMM yyyy")}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-1 lg:col-span-2">
            <CardHeader className="pb-0 flex flex-row items-center justify-between text-left">
              <CardTitle className="text-lg">Members</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <MemberList
                chitPoolId={chitPool.id}
                chitPool={chitPool}
                members={chitPool.members}
                onAddMember={handleAddMember}
                onUpdateMember={handleUpdateMember}
                onDeleteMember={handleDeleteMember}
                onToggleLift={handleToggleLift}
                onUpdateDisbursedAmount={handleUpdateDisbursedAmount}
                onPaymentUpdate={fetchData}
              />
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Monthly Amounts Dialog */}
      <Dialog open={showMonthlyAmountsDialog} onOpenChange={setShowMonthlyAmountsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Monthly Contribution Schedule</DialogTitle>
            <DialogDescription>
              Amount to be collected for each month
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 max-h-[400px] overflow-y-auto space-y-2">
            {chitPool?.monthlyAmounts?.map((item: any) => {
              const dueDate = addMonths(new Date(chitPool.firstDueDate), item.month - 1);
              return (
                <div key={item.month} className="flex justify-between py-2 px-3 rounded-lg bg-muted/50">
                  <span className="text-sm font-medium">{format(dueDate, "MMM dd, yyyy")}</span>
                  <span className="text-sm text-primary font-semibold">
                    ₹{item.amount.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total Amount</span>
              <span className="text-lg font-bold text-primary">
                ₹{chitPool?.monthlyAmounts?.reduce((sum: number, item: any) => sum + item.amount, 0).toLocaleString()}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AnimatedPage>
  );
};

export default ChitPoolDetails;
